import type { ModuleId } from './module-labels';

export type ModuleDependencies = Partial<Record<ModuleId, readonly ModuleId[]>>;

/**
 * Modules that can't be used on their own. Selecting a module that appears as a key also selects the modules it
 * maps to, e.g. the enteric methane and manure management modules for a livestock type go together.
 */
export const MODULE_DEPENDENCIES: ModuleDependencies = {
  // Enteric methane needs the matching manure management module
  '3.1': ['4.1'],
  '3.2': ['4.2'],
  '3.3': ['4.3'],
  '3.4': ['4.4'],
  '3.5': ['4.5'],
  '3.6': ['4.7'],
  // And vice versa. Poultry (4.6) has no enteric methane module.
  '4.1': ['3.1'],
  '4.2': ['3.2'],
  '4.3': ['3.3'],
  '4.4': ['3.4'],
  '4.5': ['3.5'],
  '4.7': ['3.6'],
  '5.1': ['15.4'],
  '8.1': ['15.10'],
  '8.2': ['15.10'],
  '14.1': ['15.11'],
  '15.4': ['5.1'],
  '15.10': ['8.1', '8.2'],
  '15.11': ['14.1'],
};

export type DependentChange = {
  /** The module that was selected or deselected automatically */
  module: ModuleId;
  /** The module that requires it, whose selection or deselection caused the change */
  requiredBy: ModuleId;
};

/**
 * Listens for modules being selected or deselected and keeps the modules they depend on in step, following chains
 * of dependencies. Selecting a module selects its dependency, and deselecting it deselects its dependency unless
 * another selected module still requires it. Dependencies missing from `available` (e.g. not in this calculator) are
 * skipped.
 */
export function applyModuleDependencies(
  previous: readonly ModuleId[],
  next: readonly ModuleId[],
  available: readonly ModuleId[],
  dependencies: ModuleDependencies = MODULE_DEPENDENCIES,
): { modules: ModuleId[]; added: DependentChange[]; removed: DependentChange[] } {
  const before = new Set(previous);
  const selected = new Set(next);
  const availableSet = new Set(available);
  const added: DependentChange[] = [];
  const removed: DependentChange[] = [];

  // Both lists grow as dependencies change, so they get to update their own dependencies too
  const enabled = next.filter((module) => !before.has(module));
  for (const module of enabled) {
    for (const dependency of dependencies[module] ?? []) {
      if (selected.has(dependency) || !availableSet.has(dependency)) continue;
      selected.add(dependency);
      added.push({ module: dependency, requiredBy: module });
      enabled.push(dependency);
    }
  }

  const isStillRequired = (dependency: ModuleId) =>
    [...selected].some((module) => module !== dependency && dependencies[module]?.includes(dependency));

  const disabled = previous.filter((module) => !selected.has(module));
  for (const module of disabled) {
    for (const dependency of dependencies[module] ?? []) {
      if (!selected.has(dependency) || isStillRequired(dependency)) continue;
      selected.delete(dependency);
      removed.push({ module: dependency, requiredBy: module });
      disabled.push(dependency);
    }
  }

  return { modules: [...selected], added, removed };
}
